import { type ZodObject, z } from "zod";

// biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
export type SchemaWithDynamicUtils<T extends ZodObject<any>> = T & {
  /**
   * オブジェクト直下のフィールド総数を計算します。
   * ネストされたオブジェクトや配列は1つのフィールドとしてカウントされます。
   * options.ignoreCondition で true を返したフィールドは除外されます。
   * options.excludeFromFlatCount で true を返したフィールドも除外されます。
   */
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getTotalFields: (data: any) => number;
  /**
   * オブジェクト直下の入力済みフィールド数を計算します。
   * ネストされたオブジェクトや配列は、中身が入っていれば入力済みとみなされ1としてカウントされます。
   * options.ignoreCondition または options.excludeFromFlatCount で除外されたフィールドはカウントされません。
   */
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getInputCount: (data: any) => number;
  /**
   * 必須フィールドの総数を返します（options.requiredFields の長さ）。
   */
  getRequiredTotalFields: () => number;
  /**
   * 必須フィールドのうち、入力済みの数を返します。
   */
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getRequiredInputCount: (data: any) => number;
  /**
   * ネストされた構造を含めた全フィールド数を再帰的に計算します。
   * 末端のフィールドまで探索してカウントします。
   * options.ignoreCondition で true を返したフィールドは、その配下も含めて除外されます。
   * options.excludeFromFlatCount の設定は無視され、中身のフィールドがカウントされます。
   */
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getTotalNestedFields: (data: any) => number;
  /**
   * ネストされた構造を含めた入力済みフィールド数を再帰的に計算します。
   * 末端のフィールドまで探索してカウントします。
   */
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getInputNestedCount: (data: any) => number;
  /**
   * ネストされた構造を含めた必須フィールドの入力済み数を計算します。
   * パス指定された必須フィールドに対応しています。
   */
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getRequiredNestedInputCount: (data: any) => number;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
export function extendDynamicSchema<T extends ZodObject<any>>(
  schema: T,
  options: {
    /**
     * フィールドを計算から完全に除外するための条件関数。
     * true を返すと、全てのカウントメソッド（getTotalFields, getInputCount, getTotalNestedFields など）の計算対象から外れます。
     */
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    ignoreCondition?: (data: any, fieldName: string) => boolean;
    /**
     * 必須フィールドのパス（文字列）の配列。
     * getRequiredTotalFields, getRequiredInputCount などで使用されます。
     */
    requiredFields?: string[];
  } = {},
): SchemaWithDynamicUtils<T> {
  const keys = Object.keys(schema.shape);
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  const shouldInclude = (data: any, key: string) =>
    !options.ignoreCondition || !options.ignoreCondition(data, key);

  // スキーマ定義から、そのフィールドが「オブジェクトの配列」かどうかを判定する
  const isObjectArrayField = (key: string): boolean => {
    const fieldSchema = schema.shape[key];
    if (!fieldSchema) return false;

    let s = fieldSchema;
    // Optional / Nullable を剥がす
    while (s instanceof z.ZodOptional || s instanceof z.ZodNullable) {
      s = s.unwrap();
    }

    if (s instanceof z.ZodArray) {
      let element = s.element;
      while (
        element instanceof z.ZodOptional ||
        element instanceof z.ZodNullable
      ) {
        element = element.unwrap();
      }
      // 要素がObjectなら動的リストとみなす
      return element instanceof z.ZodObject;
    }

    return false;
  };

  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  const isFilled = (value: any) =>
    // dynamicSchemaを用いるフォームでは、boolean型はトグルスイッチで表されるため、falseを未入力とみなす
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== false &&
    // 配列の場合は空でないことを確認
    (!Array.isArray(value) || value.length > 0);
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  const countArrayField = (array: any[]): number =>
    array.reduce((count, item) => {
      if (typeof item === "object" && item !== null) {
        return (
          count +
          Object.keys(item).filter((k) => shouldInclude(item[k], k)).length
        );
      }
      return count + 1;
    }, 0);

  // 入れ子構造の個別フィールドを再帰的にカウントする関数
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> データ構造が動的なため
  const countNestedFields = (obj: any, countFilled = false): number => {
    const countRecursively = (
      // biome-ignore lint/suspicious/noExplicitAny: <explanation> データ構造が動的なため
      currentValue: any,
      currentPath: string,
    ): number => {
      if (currentPath && !shouldInclude(obj, currentPath)) {
        return 0;
      }

      if (!isFilled(currentValue)) {
        return countFilled ? 0 : 1;
      }

      if (Array.isArray(currentValue)) {
        // パスがトップレベルの場合はスキーマ定義を確認、それ以外は簡易チェック
        // (入れ子パスに対するスキーマ定義取得は複雑なため、一旦トップレベルフィールドのみ isObjectArrayField で判定)
        // 本来はネストされたパスに対してもスキーマを確認すべきだが、再帰的な型探索が必要になる。
        // ここでは簡易的に、直下の配列については既存のロジック（要素数分カウント）を維持しつつ、
        // トップレベルの配列フィールドについてのみスキーマベースの判定を入れたいが、
        // countNestedFieldsは再帰呼び出しされるので currentPath を使う必要がある。
        // しかし、この関数は現在使われているかわからない（getTotalNestedFieldsはProgressCardで使われていない）。
        // ProgressCardは getTotalFields / getInputCount を使っている。

        // ネストされたフィールドのカウントについては、一旦既存の実装を維持しつつ、
        // マルチセレクト（プリミティブ配列）を単一フィールドとして扱いたい。
        // currentPathが空ならトップレベルだが、ここは再帰呼び出しの中。
        // 簡易的に「配列の要素がオブジェクトでなければ単一フィールド扱い」とする。

        if (currentValue.length === 0) {
          return countFilled ? 0 : 1;
        }

        // 配列の要素がオブジェクトであるかどうかで判断
        const isObjectArray = currentValue.some(
          (item) => typeof item === "object" && item !== null,
        );

        if (!isObjectArray) {
          // オブジェクトでない場合は、配列１つで１カウントとして扱う
          // CountFilledの場合、値が１つでも入力されていれば１としてカウントする
          return !countFilled || currentValue.some((item) => isFilled(item))
            ? 1
            : 0;
        }

        // 配列の各要素を再帰的にカウント
        return currentValue.reduce((count: number, item, index) => {
          const itemPath = currentPath ? `${currentPath}.${index}` : `${index}`;
          return count + countRecursively(item, itemPath);
        }, 0);
      }

      if (typeof currentValue === "object") {
        return Object.entries(currentValue).reduce(
          (count: number, [key, value]) => {
            const fieldPath = currentPath ? `${currentPath}.${key}` : key;
            return count + countRecursively(value, fieldPath);
          },
          0,
        );
      }

      return 1;
    };

    return countRecursively(obj, "");
  };

  // パス形式のフィールド名から値を取得する関数
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> データ構造が動的なため
  const getValueByPath = (obj: any, path: string): any => {
    const parts = path.split(".");
    let current = obj;

    for (const part of parts) {
      if (
        current === null ||
        current === undefined ||
        typeof current !== "object"
      ) {
        return undefined;
      }
      current = current[part];
    }

    return current;
  };

  return Object.assign(schema, {
    // 元の実装（親オブジェクトレベルでのカウント）
    // 総フィールド数
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getTotalFields: (data: any) =>
      keys
        .filter((key) => shouldInclude(data, key))
        .reduce((count, key) => {
          const value = data[key];
          // 配列系のフィールド（e.g. storage, interface）は全要素の総フィールド数を計算
          // ただし、スキーマ定義上「オブジェクトの配列」である場合のみ（動的リスト）。
          // プリミティブの配列（マルチセレクト）は単一フィールドとして扱う。
          return Array.isArray(value) && isObjectArrayField(key)
            ? count + countArrayField(value)
            : count + 1;
        }, 0),

    // 入力済みフィールド
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getInputCount: (data: any) =>
      keys
        .filter((key) => shouldInclude(data, key) && isFilled(data[key]))
        .reduce((count, key) => {
          const value = data[key];
          // 配列系のフィールド（e.g. storage, interface）は各要素に同様のフィルタを走らせた上で総フィールド数を計算
          // ただし、スキーマ定義上「オブジェクトの配列」である場合のみ（動的リスト）。
          return Array.isArray(value) && isObjectArrayField(key)
            ? count +
                value.reduce((sum, item) => {
                  if (typeof item === "object" && item !== null) {
                    return (
                      sum +
                      Object.keys(item).filter(
                        (k) => shouldInclude(data, k) && isFilled(item[k]),
                      ).length
                    );
                  }
                  return sum;
                }, 0)
            : count + 1;
        }, 0),

    // 必須項目の総数
    getRequiredTotalFields: () => (options.requiredFields || []).length,

    // 必須項目の入力済み数
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getRequiredInputCount: (data: any) =>
      (options.requiredFields || []).filter((key) => {
        const value = data[key];
        if (Array.isArray(value)) {
          return value.length > 0; // 配列に1つ以上要素があれば入力済みと見なす
        }
        return isFilled(value);
      }).length,

    // 新しい実装（入れ子構造の個別フィールドをカウント）
    // 総フィールド数（入れ子構造の個別フィールドをカウント）
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getTotalNestedFields: (data: any) => countNestedFields(data, false),

    // 入力済みフィールド（入れ子構造の個別フィールドをカウント）
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getInputNestedCount: (data: any) => countNestedFields(data, true),

    // 必須項目の入力済み数（パス形式の必須フィールドに対応）
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getRequiredNestedInputCount: (data: any) =>
      (options.requiredFields || []).filter((fieldPath) => {
        const value = getValueByPath(data, fieldPath);
        if (Array.isArray(value)) {
          return value.length > 0; // 配列に1つ以上要素があれば入力済みと見なす
        }
        return isFilled(value);
      }).length,
  });
}
